import type { SimulationState, ThemeData, StaffDemand } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

function calculateSatisfaction(
  staff: { salary: number; desiredSalary: number; morale: number; energy: number },
  officePerks: string[],
): number {
  // Salary ratio: below 0.8 = unhappy, above 1.2 = very happy
  const salaryRatio = staff.desiredSalary > 0
    ? Math.min(staff.salary / staff.desiredSalary, 1.5)
    : 1.0;
  const salaryScore = salaryRatio * 40; // 0-60

  // Morale contribution
  const moraleScore = (staff.morale / 100) * 30; // 0-30

  // Energy contribution (overwork penalty)
  const energyScore = (staff.energy / 100) * 15; // 0-15

  // Office perks bonus
  const perkBonus = Math.min(officePerks.length * 3, 15); // 0-15

  return Math.min(100, Math.max(0, salaryScore + moraleScore + energyScore + perkBonus));
}

function generateDemand(
  staffSalary: number,
  desiredSalary: number,
  rng: SeededRandom,
  themeData: ThemeData,
): StaffDemand {
  const types: StaffDemand['type'][] = ['raise', 'better_office', 'less_workload', 'promotion', 'new_equipment'];

  // Weight toward raise if underpaid
  let type: StaffDemand['type'];
  if (desiredSalary > staffSalary * 1.1) {
    type = rng.nextBool(0.5) ? 'raise' : rng.pick(types);
  } else {
    type = rng.pick(types);
  }

  return {
    type,
    description: '', // Filled from strings.json at UI layer
    requiredValue: type === 'raise' ? Math.round(desiredSalary * 1.1) : undefined,
    daysToRespond: themeData.balance.demandResponseDays,
    ticksRemaining: themeData.balance.demandResponseDays * themeData.balance.ticksPerDay,
  };
}

export function moraleSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];
  const moraleDrainMultiplier = state.company.isHardMode
    ? themeData.balance.hardMode.moraleDrainMultiplier
    : 1.0;
  const demandCooldownTicks =
    themeData.balance.demandCooldownWeeks * themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;

  for (const staff of state.staff) {
    if (staff.isCEO) continue; // CEO never has morale issues
    if (staff.isTraining || staff.isOnLeave) continue;

    // Update satisfaction
    staff.satisfaction = calculateSatisfaction(staff, state.office.perks);

    // Morale drifts toward satisfaction
    const moraleDrain = themeData.balance.moraleDrainPerTick * moraleDrainMultiplier;
    if (staff.satisfaction < staff.morale) {
      staff.morale = Math.max(0, staff.morale - moraleDrain);
    } else if (staff.satisfaction > staff.morale) {
      staff.morale = Math.min(100, staff.morale + moraleDrain * 0.5);
    }

    // Office perk morale boost (per-tick fraction of weekly bonus)
    if (themeData.entities.perkEffects) {
      let perkMoralePerWeek = 0;
      for (const perk of state.office.perks) {
        const effect = themeData.entities.perkEffects[perk];
        if (effect) perkMoralePerWeek += effect.moraleBonus;
      }
      if (perkMoralePerWeek > 0) {
        const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
        staff.morale = Math.min(100, staff.morale + perkMoralePerWeek / ticksPerWeek);
      }
    }

    // Demand countdown
    if (staff.demand) {
      staff.demand.ticksRemaining--;
      if (staff.demand.ticksRemaining <= 0) {
        // Demand expired — treated as deny
        staff.morale = Math.max(0, staff.morale - 25);
        staff.satisfaction = Math.max(0, staff.satisfaction - 20);
        staff.isOnNotice = true;
        staff.noticeDaysRemaining = themeData.balance.departureNoticeDays;
        events.push({
          type: 'STAFF_ON_NOTICE',
          staffId: staff.id,
          name: staff.name,
          daysRemaining: staff.noticeDaysRemaining,
        });
        staff.demand = null;
      }
    }

    // Notice countdown → departure
    if (staff.isOnNotice) {
      // Decrement once per day (every ticksPerDay ticks)
      if (state.company.currentTick % themeData.balance.ticksPerDay === 0) {
        staff.noticeDaysRemaining--;
      }
      if (staff.noticeDaysRemaining <= 0) {
        // Staff leaves
        if (staff.teamId) {
          const team = state.teams.find((t) => t.id === staff.teamId);
          if (team) {
            // Disruption penalty: add bugs to active project when a team member departs
            if (team.assignedProjectId) {
              const project = state.projects.find((p) => p.id === team.assignedProjectId);
              if (project && project.phase !== 'complete') {
                const disruptionBugs = 5 + (staff.level ?? 1) * 0.5;
                project.bugs += disruptionBugs;
                events.push({
                  type: 'PROJECT_DISRUPTED',
                  projectId: project.id,
                  name: project.name,
                  staffName: staff.name,
                  bugsAdded: disruptionBugs,
                });
              }
            }
            team.memberIds = team.memberIds.filter((id) => id !== staff.id);
            if (team.leaderId === staff.id) team.leaderId = null;
          }
        }
        // Remove from contracts
        for (const contract of state.contracts) {
          contract.assignedStaffIds = contract.assignedStaffIds.filter((id) => id !== staff.id);
        }
        state.staff = state.staff.filter((s) => s.id !== staff.id);

        events.push({
          type: 'STAFF_LEFT',
          staffId: staff.id,
          name: staff.name,
          reason: 'Demand not met',
        });

        // Morale hit to remaining staff
        for (const remaining of state.staff) {
          if (!remaining.isCEO) {
            remaining.morale = Math.max(0, remaining.morale - 12);
          }
        }
        continue; // Skip demand generation for departed staff
      }
    }

    // Generate new demand if satisfaction is low enough
    if (
      !staff.demand &&
      !staff.isOnNotice &&
      staff.satisfaction < themeData.balance.demandSatisfactionThreshold
    ) {
      // Check cooldown — only generate once per cooldown period
      // Use tick modulo as a simple cooldown (not per-staff tracking, but close enough)
      const staffHash = staff.id.charCodeAt(staff.id.length - 1) ?? 0;
      const cooldownOffset = staffHash * 7; // Stagger demands across staff
      if ((state.company.currentTick + cooldownOffset) % demandCooldownTicks < 1) {
        staff.demand = generateDemand(staff.salary, staff.desiredSalary, rng, themeData);
        events.push({
          type: 'STAFF_DEMAND',
          staffId: staff.id,
          name: staff.name,
          demand: staff.demand,
        });
      }
    }
  }

  // Leave countdown
  for (const staff of state.staff) {
    if (staff.isOnLeave) {
      staff.leaveTicksRemaining--;
      staff.energy = Math.min(100, staff.energy + themeData.balance.energyRecoveryPerTick);
      staff.morale = Math.min(100, staff.morale + 0.2);

      if (staff.leaveTicksRemaining <= 0) {
        staff.isOnLeave = false;
        events.push({ type: 'LEAVE_ENDED', staffId: staff.id, name: staff.name });
      }
    }
  }

  return events;
}
